import React, { useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-hot-toast';

const PasswordReset = () => {
    const { uid, token } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState('');

    const validatePassword = (password) => {
        const minLength = 14;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        const errors = [];
        if (password.length < minLength) errors.push(`At least ${minLength} characters`);
        if (!hasUpperCase) errors.push('One uppercase letter');
        if (!hasLowerCase) errors.push('One lowercase letter');
        if (!hasNumbers) errors.push('One number');
        if (!hasSpecialChar) errors.push('One special character');

        return errors;
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        // Clear any prior server-side error as soon as the user starts editing again
        if (serverError) {
            setServerError('');
        }
    };

    const normalizeErrorMessage = (raw) => {
        if (!raw) return 'Failed to reset password. Please try again.';

        // If backend ever sends an array, use the first element
        if (Array.isArray(raw)) {
            raw = raw[0];
        }

        if (typeof raw !== 'string') {
            return 'Failed to reset password. Please try again.';
        }

        // Sometimes errors come back stringified like ["message"]
        const trimmed = raw.trim();
        if ((trimmed.startsWith('[') && trimmed.endsWith(']')) ||
            (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'string') {
                    return parsed[0];
                }
            } catch {
                // Fallback: strip outer brackets/quotes
                return trimmed.replace(/^\["?/, '').replace(/"?]$/, '');
            }
        }

        return raw;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const { password, confirmPassword } = formData;
        const passwordErrors = validatePassword(password);

        if (passwordErrors.length > 0) {
            toast.error(
                <div>
                    <p>Password must contain:</p>
                    <ul>
                        {passwordErrors.map((error, index) => (
                            <li key={index}>• {error}</li>
                        ))}
                    </ul>
                </div>
            );
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            setLoading(false);
            return;
        }

        try {
            const apiUrl = import.meta.env.VITE_API_URL;
            const response = await axios.post(
                `${apiUrl}/api/password_reset_confirm/`,
                { uidb64: uid, token, password },
                { withCredentials: true }
            );

            toast.success('Password reset successful!');
            // Keep button disabled after success
            setTimeout(() => navigate('/login'), 2000);
        } catch (error) {
            console.error('Error:', error);
            let errorMessage = 'Failed to reset password. Please try again.';
            
            // Handle different error response formats
            if (error.response?.data) {
                // Handle the specific error structure for password reuse
                if (error.response.data.error?.new_password2) {
                    errorMessage = normalizeErrorMessage(error.response.data.error.new_password2);
                }
                // Handle other error formats
                else if (error.response.data.error) {
                    const firstErrorKey = Object.keys(error.response.data.error)[0];
                    if (firstErrorKey && Array.isArray(error.response.data.error[firstErrorKey])) {
                        errorMessage = normalizeErrorMessage(error.response.data.error[firstErrorKey]);
                    } else if (typeof error.response.data.error === 'string') {
                        errorMessage = normalizeErrorMessage(error.response.data.error);
                    }
                }
                // Handle simple message format
                else if (error.response.data.message) {
                    errorMessage = normalizeErrorMessage(error.response.data.message);
                }
            }

            // Show a concise toast and put the detailed message near the field
            setServerError(errorMessage);
            toast.error('Unable to reset password. Please review the message above the form.');
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-start justify-center pt-16 px-4 sm:px-6 lg:px-8">
            <div className="w-full max-w-lg space-y-8 bg-white dark:bg-gray-800 p-12 rounded-2xl shadow-xl">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-semibold text-primary-700">
                        Reset Your Password
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Please enter your new password below
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                New Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    id="password"
                                    required
                                    className="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    value={formData.password}
                                    onChange={handleChange}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-5 w-5 text-gray-400" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400" />
                                    )}
                                </button>
                            </div>
                            {serverError && (
                                <p className="mt-1 text-sm text-red-600">
                                    {serverError}
                                </p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                Confirm New Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    id="confirmPassword"
                                    required
                                    className="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                                loading ? 'bg-primary-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'
                            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
                        >
                            <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                <Lock className="h-5 w-5 text-primary-500 group-hover:text-primary-400" />
                            </span>
                            {loading ? 'Resetting...' : 'Reset Password'}
                        </button>
                    </div>
                </form>

                <div className="text-center">
                    <p className="mt-2 text-sm text-gray-600">
                        Remember your password?{' '}
                        <a href="/login" className="font-medium text-primary-600 hover:text-primary-700">
                            Sign in
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PasswordReset;
